import defaultResource from './locales/default.json';
import modelResource from './locales/model.json';
import locationTypeResource from './locales/location_type.json';
import integrationResource from './locales/integration.json';
import pollRequestResource from 'infras/i18n/locales/poll_request.json';
import i18next from 'i18next';
import { Settings } from 'luxon';
import parser from 'accept-language-parser';
import { getDateTime } from 'infras/datetime';
import { secureRandomString } from 'domains/models';
import { Currency } from 'domains/models/subscription/planId';

export const supportLocales = ['en', 'ja'];
export const jaLocale = 'ja';
export const defaultLocale = jaLocale;
export const fallbackLocale = 'en';

export const supportCurrencies: Currency[] = [Currency.JPY, Currency.USD];
export const defaultCurrency = supportCurrencies[0];
export const failoverCurrency = supportCurrencies[supportCurrencies.length - 1];

export const currentLocale = (req?: any) => {
  if (!req) {
    return defaultLocale;
  }
  const acceptLanguage = req.headers['accept-language'];
  return (
    parser.pick(supportLocales, acceptLanguage, { loose: true }) ||
    defaultLocale
  );
};

export function changeLanguage(locale: string) {
  i18next.changeLanguage(locale);
  Settings.defaultLocale = locale;
}

export const translateRaw = (key: string, options?: any) => {
  return translate(key, options, true);
};

export const translate = (
  key: string,
  options?: any,
  isRaw: boolean = false,
) => {
  const newOptions: any = { ...options };
  if (isRaw === true) {
    newOptions['interpolation'] = { escapeValue: false };
  }
  const text = i18next.t(key, newOptions);
  if (['development'].includes(process.env['NODE_ENV'])) {
    const namespace = 'translation';
    if (!i18next.exists(`${namespace}:${key}`)) {
      console.warn(`Translation is not found: ${namespace} > ${key}`);
    }
  }
  return text;
};

export const addResources = (
  resources: { [language: string]: any },
  namespace: string | null = null,
) => {
  for (const lang in resources) {
    i18next.addResourceBundle(
      lang,
      namespace || 'translation',
      resources[lang],
      true,
      true,
    );
  }
};

export const translateInComponent = (translations: any | null = null) => {
  const namespace = secureRandomString();
  return translateInNamespace(namespace, translations);
};

export const translateInNamespace = (
  namespace: string,
  translations: any | null = null,
) => {
  if (translations) {
    if (Array.isArray(translations)) {
      for (const t of translations) {
        addResources(t, namespace);
      }
    } else {
      addResources(translations, namespace);
    }
  }
  const t = i18next.getFixedT(null, namespace);
  return (key: string, options: any = {}, isRaw: boolean = false) => {
    const newOptions: any = { ...options };
    if (isRaw === true) {
      newOptions['interpolation'] = { escapeValue: false };
    }
    const text = t(key, newOptions);
    if (['development'].includes(process.env['NODE_ENV'])) {
      if (!i18next.exists(`${namespace}:${key}`)) {
        console.warn(`Translation is not found: ${namespace} > ${key}`);
      }
    }
    return text;
  };
};

i18next.init({
  fallbackLng: defaultLocale,
  resources: {},
  interpolation: {
    format: function (value, format) {
      if (format === 'uppercase') {
        return value.toUpperCase();
      }
      if (value instanceof Date && format) {
        return getDateTime(value).toFormat(format);
      }
      return value;
    },
  },
});
addResources(defaultResource);
addResources(modelResource);
addResources(locationTypeResource);
addResources(integrationResource);
addResources(pollRequestResource);
if (typeof window === 'undefined') {
  const mailerResource = require('./locales/mailer.json');
  addResources(mailerResource);
  const pushNotificationResource = require('./locales/push_notification.json');
  addResources(pushNotificationResource);
}
